import { calculateAssignedOptionScore } from '../context';

const currentDateString = () =>
  new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });

export const scoringStep = {
  id: 'scoring',

  buildPrompt(ctx) {
    const { prompts, computed, questionnaire } = ctx;
    const parts = [prompts.analysisBasePrompt, prompts.languageInstruction];
    if (prompts.scoringPrompt) parts.push(`PUNKTEVERGABE:\n${prompts.scoringPrompt}`);
    parts.push(
      `Datum: ${currentDateString()}. Fragebogen: "${questionnaire.title}".

${computed.qaPairs.map((p) => p.info).join('\n')}

Schritt 1 — Punktevergabe pro Frage.
Bewerte jede Antwort einzeln. Gib pro Frage:
- questionId (übernimm die ID aus den eckigen Klammern)
- score (0 bis max)
- maxScore (übernimm die angegebene Maximalpunktzahl)
- reason: 1-2 prägnante Sätze auf Deutsch, warum die Punkte so vergeben werden — Pflichtfeld, NIE leer lassen
- redFlag: true NUR wenn die Antwort ein ernstes Ausschlusskriterium oder gravierendes Warnsignal darstellt (z. B. fehlende zwingende Voraussetzung, widersprüchliche oder bedenkliche Angabe). Sonst false.

Bei Fragen mit festgelegten Punkten pro Option: Verwende die angegebenen Punkte.
Bei Ranking-Fragen: Leite aus den Punkten pro Option die ideale Reihenfolge ab (höchste Punkte = Rang 1). Bewerte, wie gut der Bewerber die wichtigeren (höher bepunkteten) Optionen nach oben sortiert, und vergib entsprechend 0 bis max Punkte.
WICHTIG: Das scores-Array MUSS für JEDE oben gelistete Frage genau einen Eintrag enthalten — keine Frage auslassen, auch Ranking-Fragen nicht. Übernimm die questionId exakt zeichengleich aus den eckigen Klammern.

In den nächsten Schritten kommen Qualifikation und Gesamtbewertung — konzentriere Dich JETZT ausschließlich auf die Punktevergabe pro Frage.`
    );
    return parts.join('\n\n');
  },

  schema: {
    type: 'OBJECT',
    properties: {
      scores: {
        type: 'ARRAY',
        items: {
          type: 'OBJECT',
          properties: {
            questionId: { type: 'STRING' },
            score: { type: 'NUMBER' },
            maxScore: { type: 'NUMBER' },
            reason: { type: 'STRING' },
            redFlag: { type: 'BOOLEAN' }
          },
          // Gemini drops un-required keys at random; `reason`/`redFlag` were the
          // "silently trimmed" fields. Force every per-question key to be emitted.
          propertyOrdering: ['questionId', 'score', 'maxScore', 'reason', 'redFlag'],
          required: ['questionId', 'score', 'maxScore', 'reason', 'redFlag']
        }
      }
    },
    required: ['scores']
  },

  parse(json, ctx) {
    const aiScores = Array.isArray(json?.scores) ? json.scores : [];
    const aiById = new Map(
      aiScores.filter((s) => s && s.questionId).map((s) => [String(s.questionId), s])
    );

    const initial = ctx.computed.qaPairs.map(({ questionId, maxScore }) => {
      const ai = aiById.get(String(questionId));
      const numeric = Number(ai?.score);
      const bounded = Number.isFinite(numeric)
        ? Math.min(Math.max(0, Math.round(numeric)), maxScore)
        : 0;
      return {
        questionId,
        score: bounded,
        maxScore,
        reason: ai?.reason || '',
        source: 'ai',
        redFlag: ai?.redFlag ?? false
      };
    });

    // If the question has predefined option scores, those override the AI's
    // number — the AI keeps the reason text but the score becomes deterministic.
    const processedScores = initial.map((item) => {
      const question = ctx.computed.questionById.get(String(item.questionId));
      const answer = ctx.submission.answers[item.questionId];
      const assigned = calculateAssignedOptionScore(question, answer, item.maxScore);
      if (assigned !== null) {
        return { ...item, score: assigned, source: 'ai' };
      }
      return item;
    });

    return { processedScores };
  }
};
